import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, getAdminToken } from "@/lib/adminApi";
import { AGS_LOGO_URL } from "@/lib/brand";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (getAdminToken()) {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await adminLogin(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed";
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#0A0A0A] text-white flex aaa-noise overflow-hidden">
      {/* Left editorial panel */}
      <div className="hidden md:flex md:flex-col md:w-1/2 bg-[#0E2A57] p-12 lg:p-16 justify-between relative">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(190,231,245,0.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(190,231,245,0.6) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
        <div className="relative">
          <img src={AGS_LOGO_URL} alt="AGS" className="h-16 w-auto object-contain brightness-0 invert" />
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] mt-10">
            01 — Admin Console
          </div>
          <h1 className="mt-6 font-heading font-extrabold text-5xl md:text-6xl leading-[0.95] tracking-[-0.04em]">
            Family<br />
            <span className="italic-display font-normal text-[#BEE7F5]">business</span><br />
            standards.
          </h1>
          <p className="mt-8 text-white/65 text-base leading-[1.6] max-w-md">
            Internal dashboard for the AGS family to review quote requests,
            job applications, and contact messages from across Ireland.
          </p>
        </div>
        <div className="relative font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
          AGS Cleaning Services · Family-owned · Ireland
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <form onSubmit={onSubmit} data-testid="admin-login-form" className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-2">
            <Lock size={14} className="text-[#2BB6E5]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2BB6E5]">
              Restricted Access
            </span>
          </div>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-[-0.035em] text-white">
            Sign in to the<br />
            <span className="italic-display font-normal text-[#BEE7F5]">AGS console.</span>
          </h2>

          <div className="mt-10 space-y-5">
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="admin@ags-services.ie"
                data-testid="admin-login-email"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white placeholder:text-white/30 focus:border-[#2BB6E5] focus:outline-none transition-colors"
              />
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="•••••••••"
                data-testid="admin-login-password"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white placeholder:text-white/30 focus:border-[#2BB6E5] focus:outline-none transition-colors"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-10 w-full inline-flex items-center justify-center gap-3 bg-white text-[#0E2A57] hover:bg-[#BEE7F5] disabled:opacity-60 disabled:cursor-not-allowed px-10 py-5 font-semibold tracking-wide transition-colors group"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> Authenticating…</>
            ) : (
              <>Sign in <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" /></>
            )}
          </button>

          <a href="/" className="mt-10 inline-block font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-white transition-colors">
            ← Back to public site
          </a>
        </form>
      </div>
    </div>
  );
}
